import { IDataEvent } from "@multiweb/types/contracts";

/**
 * Listener pro DataEvent
 * @includeToDoc
 */
export type DataEventListener = (dataEvent: IDataEvent) => void;

/**
 * Manažer umožňující jednodušší registrování naslouchání systémovým i jiným DataEventům
 * @includeToDoc
 */
export interface IDataEventsManager {
    /**
     * Registrace naslouchání obecnému DataEventu
     * @param source Pojmenování zdroje zpráv (pro systémové zprávy je to "OW", pro ostatní podle jejich nastavení)
     * @param type Typ zprávy
     * @param subType Volitelný podtyp zprávy - může zpřesňovat naslouchání jen menší množině zpráv ("*" značí naslouchání všemu)
     * @param listener Listener volaný při obdržení zprávy
     */
    subscribeDataEvent(source: string, type: string, subType: string, listener: DataEventListener): void;
    /**
     * Odregistrování naslouchání obecnému DataEventu
     * @param source Pojmenování zdroje zpráv (pro systémové zprávy je to "OW", pro ostatní podle jejich nastavení)
     * @param type Typ zprávy
     * @param subType Volitelný podtyp zprávy - může zpřesňovat naslouchání jen menší množině zpráv ("*" značí naslouchání všemu)
     * @param listener Listener volaný při obdržení zprávy
     */
    unsubscribeDataEvent(source: string, type: string, subType: string, listener: DataEventListener): void;

    /**
     * Událost vyvolávaná při změně hodnot atributů aplikačních objektů
     * @param className Název sledované třídy nebo * pro všechny
     * @param listener Listener
     */
    subscribeObjectsChanges(className: string, listener: DataEventListener): void;
    /**
     * Odregistrování události vyvolávané při vytvoření nebo smazání záznamu aplikační třídy
     * @param className Název sledované třídy nebo * pro všechny
     * @param listener Listener
     */
    unsubscribeObjectsChanges(className: string, listener: DataEventListener): void;
    /**
     * Událost vyvolávaná při vytvoření nebo smazání záznamu aplikační třídy
     * @param className Název sledované třídy nebo * pro všechny
     * @param listener Listener
     */
    subscribeClassChanges(className: string, listener: DataEventListener): void;
    /**
     * Odregistrování události vyvolávané při vytvoření nebo smazání záznamu aplikační třídy
     * @param className Název sledované třídy nebo * pro všechny
     * @param listener Listener
     */
    unsubscribeClassChanges(className: string, listener: DataEventListener): void;
    /**
     * Událost vyvolávaná při vzniku požadavku na otevření formuláře z workflow
     * @param listener Listener
     */
    subscribeWFOpenForm(listener: DataEventListener): void;
    /**
     * Odregistrování události vyvolávané při vzniku požadavku na otevření formuláře z workflow
     * @param listener Listener
     */
    unsubscribeWFOpenForm(listener: DataEventListener): void;
    /**
     * Událost vyvolávaná při přegenerování systémové keše na aplikačním serveru
     * @param settingsType Typ keše, pro který se má registrovat naslouchání
     * @param listener Listener
     */
    subscribeSystemCacheReloaded(settingsType: SysteCacheType, listener: DataEventListener): void;

    /**
     * Odregistrování události vyvolávané při přegenerování systémové keše na aplikačním serveru
     * @param settingsType Typ keše, pro který se má registrovat naslouchání
     * @param listener Listener
     */
    unsubscribeSystemCacheReloaded(settingsType: SysteCacheType, listener: DataEventListener): void;

    /**
     * Událost vyvolávaná při změně aktivních rolí
     * @param listener Listener
     */
    subscribeActiveRolesChanged(listener: DataEventListener): void;

    /**
     * Odregistrování události vyvolávané při změně aktivních rolí
     * @param listener Listener
     */
    unsubscribeActiveRolesChanged(listener: DataEventListener): void;

    /**
     * Spustí sledování změn aplikačních instancí
     */
    watchObjectsChanges(): void;
}

export class IDataEventsManager {
    private static _methods: (keyof IDataEventsManager)[] = [
        "subscribeDataEvent",
        "unsubscribeDataEvent",
        "subscribeObjectsChanges",
        "unsubscribeObjectsChanges",
        "subscribeClassChanges",
        "unsubscribeClassChanges",
        "subscribeWFOpenForm",
        "unsubscribeWFOpenForm",
        "subscribeSystemCacheReloaded",
        "unsubscribeSystemCacheReloaded",
        "subscribeActiveRolesChanged",
        "unsubscribeActiveRolesChanged",
        "watchObjectsChanges",
    ];
    private static _properties: (keyof IDataEventsManager)[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null
            && !this._properties.some((value) => !Reflect.has(instance, value))
            && !this._methods.some(value => typeof instance[value] !== "function");
    }
}

export type SysteCacheType = "Rights" | "Workflows" | "Localizations" | "Forms" | "DataModel";
